import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import pitchImage from "../../assets/images/pitch.png";
import "./pitchMap.css";
import { GpsCalibrationPoints, GpsPoint, HalfEvent } from "refsix-js-models";
import StatsPeriodToggle from "./statsPeriodToggle";
import HeatMapRenderer, { DataPoint } from "heatmap-ts";
import { useDimensions } from "../hooks";
import { useInView } from "../hooks/useInView";
import {
    generatePlottingDataForSegments,
    heatmapPointRadiusMeters,
} from "refsix-core";

interface HeatMapInterface {
    playingSegments: HalfEvent[];
    geoPoints: GpsPoint[];
    calibrationPoints: GpsCalibrationPoints;
    periodsNo: string;
    testId?: string;
}

const DEFAULT_WIDTH = 300;
const HEATMAP_INTENSITY = 0.4;
const POINT_VALUE = 100;
const ANIMATION_DURATION_MS = 1500;
const ANIMATION_STEPS = 20;
const ANIMATION_INTERVAL_MS = ANIMATION_DURATION_MS / ANIMATION_STEPS; // 75ms

const HeatMapComponent: FC<HeatMapInterface> = ({
    playingSegments,
    geoPoints,
    calibrationPoints,
    periodsNo,
    testId,
}) => {
    const [selectedSegment, setSelectedSegment] = useState<number>(0);
    const [containerDiv, dims] = useDimensions();
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const [canvasSize, setCanvasSize] = useState<[number, number]>([
        DEFAULT_WIDTH,
        100,
    ]);
    const animationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
        null
    );
    const inView = useInView(canvasRef, { threshold: 1.0 });

    const segmentsPlottedData = useMemo(() => {
        if (geoPoints && calibrationPoints && playingSegments && dims?.width) {
            return generatePlottingDataForSegments(
                geoPoints,
                calibrationPoints,
                playingSegments,
                dims?.width || DEFAULT_WIDTH
            );
        }
        return null;
    }, [geoPoints, calibrationPoints, playingSegments, dims]);

    // Set canvas and pitch image dimensions as soon as data is ready (no inView gate)
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!segmentsPlottedData || !canvas) return;

        const processedData = segmentsPlottedData[selectedSegment];
        if (!processedData?.plottingData) return;

        const width = processedData.plottingData.width;
        const height = processedData.plottingData.height;

        canvas.width = width;
        canvas.height = height;
        setCanvasSize([width, height]);
    }, [selectedSegment, segmentsPlottedData]);

    // Animate heatmap only once fully scrolled into view
    useEffect(() => {
        // Cancel any running animation
        if (animationIntervalRef.current) {
            clearInterval(animationIntervalRef.current);
            animationIntervalRef.current = null;
        }

        const canvas = canvasRef.current;
        if (!segmentsPlottedData || !canvas || !inView) return;

        const processedData = segmentsPlottedData[selectedSegment];
        if (!processedData?.plottingData) {
            const ctx = canvas.getContext("2d");
            if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
            return;
        }

        const pointRadius =
            heatmapPointRadiusMeters(processedData.plottingData.pitch) *
            (processedData.plottingData.pixelsPerMeter || 1);

        const dataPoints: DataPoint[] = processedData.plottingData.data.map(
            (point) => ({
                ...point,
                value: POINT_VALUE,
                radius: pointRadius,
            })
        );

        const width = processedData.plottingData.width;
        const height = processedData.plottingData.height;

        // Re-set canvas dimensions in case sizing effect cleared the drawing
        canvas.width = width;
        canvas.height = height;

        if (dataPoints.length === 0) return;

        // Sort by timestamp for chronological animation
        dataPoints.sort(
            (a, b) => ((a as any).timestamp || 0) - ((b as any).timestamp || 0)
        );

        const fullMax = dataPoints.length * HEATMAP_INTENSITY;
        const batchSize = Math.ceil(dataPoints.length / ANIMATION_STEPS);
        let currentIndex = 0;

        // Create renderer once and reuse for each animation step
        const heatmap = new HeatMapRenderer({
            canvas,
            width,
            height,
        });

        animationIntervalRef.current = setInterval(() => {
            const end = Math.min(
                currentIndex + batchSize,
                dataPoints.length
            );
            currentIndex = end;

            heatmap.setData({
                data: dataPoints.slice(0, currentIndex),
                max: fullMax,
            });
            heatmap.repaint();

            if (currentIndex >= dataPoints.length) {
                if (animationIntervalRef.current) {
                    clearInterval(animationIntervalRef.current);
                    animationIntervalRef.current = null;
                }
            }
        }, ANIMATION_INTERVAL_MS);
    }, [selectedSegment, segmentsPlottedData, inView]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (animationIntervalRef.current) {
                clearInterval(animationIntervalRef.current);
            }
        };
    }, []);

    return (
        <div ref={containerDiv} data-testid={testId}>
            <StatsPeriodToggle
                playingSegments={playingSegments}
                periodsNo={periodsNo}
                onSegmentChange={setSelectedSegment}
            />
            <div className="map-container">
                <img
                    src={pitchImage}
                    width={canvasSize[0]}
                    height={canvasSize[1]}
                    alt="Pitch image"
                    className="responsive svg-pitch"
                />
                <canvas ref={canvasRef} />
            </div>
        </div>
    );
};

export default HeatMapComponent;
